import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ModalKey, GetSeenModalsResponse } from '@dreamai/types';
import api from '../api';

interface OnboardingModalsContextType {
  hasSeenModal: (modalKey: ModalKey) => boolean;
  markAsSeen: (modalKey: ModalKey) => Promise<void>;
  loading: boolean;
}

const OnboardingModalsContext =
  createContext<OnboardingModalsContextType | null>(null);

interface OnboardingModalsProviderProps {
  children: React.ReactNode;
}

export const OnboardingModalsProvider: React.FC<
  OnboardingModalsProviderProps
> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [seenModals, setSeenModals] = useState<Set<ModalKey>>(new Set());
  const [loading, setLoading] = useState(true);

  /**
   * Fetch all seen modals on mount
   */
  const fetchSeenModals = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();

      const response = await api.get<GetSeenModalsResponse>('/seen-modals', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data.success) {
        setSeenModals(new Set(response.data.seenModals));
      }
    } catch (error) {
      console.error('Error fetching seen modals:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    fetchSeenModals();
  }, [fetchSeenModals]);

  /**
   * Check if a modal has been seen
   */
  const hasSeenModal = useCallback(
    (modalKey: ModalKey): boolean => {
      return seenModals.has(modalKey);
    },
    [seenModals],
  );

  /**
   * Mark a modal as seen (optimistic update)
   */
  const markAsSeen = useCallback(
    async (modalKey: ModalKey) => {
      // Optimistic update
      setSeenModals((prev) => {
        const next = new Set(prev);
        next.add(modalKey);
        return next;
      });

      try {
        const accessToken = await getAccessTokenSilently();

        await api.post(
          '/seen-modals',
          { modalKey },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
      } catch (error) {
        console.error('Error marking modal as seen:', error);
        // Revert optimistic update on error
        setSeenModals((prev) => {
          const next = new Set(prev);
          next.delete(modalKey);
          return next;
        });
      }
    },
    [getAccessTokenSilently],
  );

  const contextValue = useMemo<OnboardingModalsContextType>(
    () => ({
      hasSeenModal,
      markAsSeen,
      loading,
    }),
    [hasSeenModal, markAsSeen, loading],
  );

  return (
    <OnboardingModalsContext.Provider value={contextValue}>
      {children}
    </OnboardingModalsContext.Provider>
  );
};

/**
 * Hook to use onboarding modals context
 */
export const useOnboardingModals = (): OnboardingModalsContextType => {
  const context = useContext(OnboardingModalsContext);
  if (!context) {
    throw new Error(
      'useOnboardingModals must be used within OnboardingModalsProvider',
    );
  }
  return context;
};
